import React, { useState } from "react";
import { Alert, AlertTitle, Box, Button, LinearProgress, Typography } from "@mui/material";

import { Progress, Step } from "ui";

import { formatStepLabel } from "../taskPhases";

/** A step of a generation workflow, e.g. step 2 of 3 "Generating leadins". */
export type TaskStep = {
  label: string;
  /** 1-based position of the step among the workflow's steps. */
  index: number;
  count: number;
};

/** Where a running task is. */
export type TaskPhase = {
  /** Progress of a transfer; the bar is indeterminate without it. */
  percent?: number;
  /** The generation workflow step the task is on. */
  step?: TaskStep;
};

export type TaskAction = {
  label: string;
  onClick(): void;
  /** Whether this is the main action, shown as a contained button. */
  primary?: boolean;
};

/** Why a task failed and what the user can do about it. */
export type TaskError = {
  title: string;
  reason: string;
  actions: TaskAction[];
};

export type TaskProgressProps =
  | {
      /** Title above the bar, e.g. "Uploading addresses"; without it the progress is shown compactly, without a frame. */
      title?: string;
      phase: TaskPhase;
      /** Steps listed behind a Show steps toggle, when the bar is indeterminate. */
      steps?: Step[];
      /** Cancels the task; shown beside the title. */
      onCancel?(): void;
      error?: undefined;
    }
  | {
      /** The task failed; shown in place of its progress. */
      error: TaskError;
      title?: undefined;
      phase?: undefined;
      steps?: undefined;
      onCancel?: undefined;
    };

const TaskFailure: React.FC<{ error: TaskError }> = ({ error: { title, reason, actions } }) => (
  <Alert severity="error" variant="compact" sx={{ "& .MuiAlert-message": { flexGrow: 1 } }}>
    <AlertTitle>{title}</AlertTitle>
    {reason}
    {actions.length > 0 && (
      <Box display="flex" justifyContent="flex-end" gap={0.5} mt={0.75}>
        {actions.map(({ label, onClick, primary }) => (
          <Button key={label} size="small" variant={primary ? "contained" : "text"} onClick={onClick}>
            {label}
          </Button>
        ))}
      </Box>
    )}
  </Alert>
);

/**
 * Progress of a long-running task (upload, import or generation), or why it failed.
 */
const TaskProgress: React.FC<TaskProgressProps> = ({ title, phase, steps, onCancel, error }) => {
  const [showSteps, setShowSteps] = useState(false);

  if (error) return <TaskFailure error={error} />;

  const { step, percent } = phase;
  const isDeterminate = percent !== undefined;
  const hasSteps = !isDeterminate && !!steps?.length;
  const isFramed = title !== undefined;
  const label = title ?? step?.label ?? "In progress";

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label={label}
      display="flex"
      flexDirection="column"
      gap={0.75}
      {...(isFramed && { border: 1, borderColor: "divider", borderRadius: 1, px: 1.25, py: 1 })}
    >
      {(isFramed || onCancel) && (
        <Box display="flex" alignItems="center" gap={0.75}>
          <Typography variant="body2" fontWeight={600} flexGrow={1} minWidth={0}>
            {title}
          </Typography>
          {onCancel && (
            <Button size="small" onClick={onCancel} sx={{ py: 0.25, minWidth: 0 }}>
              Cancel
            </Button>
          )}
        </Box>
      )}
      <LinearProgress aria-label={label} variant={isDeterminate ? "determinate" : "indeterminate"} value={percent} />
      {(step || isDeterminate) && (
        <Box display="flex" justifyContent="space-between" gap={1}>
          {step && (
            <Typography variant="caption" color="text.secondary">
              {formatStepLabel(step.label, step)}
            </Typography>
          )}
          {isDeterminate && (
            <Typography variant="caption" color="text.secondary" ml="auto">
              {`${Math.round(percent)}%`}
            </Typography>
          )}
          {hasSteps && (
            <Button
              size="small"
              variant="text"
              onClick={() => setShowSteps((shown) => !shown)}
              sx={{ p: 0, minWidth: 0, fontSize: 12, lineHeight: 1.5 }}
            >
              {showSteps ? "Hide steps" : "Show steps"}
            </Button>
          )}
        </Box>
      )}
      {hasSteps && showSteps && <Progress steps={steps} />}
    </Box>
  );
};

/** A task's result, shown in place of its progress once it succeeds. */
export const TaskResult: React.FC<{ message: string }> = ({ message }) => (
  <Alert severity="success" variant="compact" role="status">
    {message}
  </Alert>
);

export default TaskProgress;
